import { CapabilityRule } from '~types/capability.type';

/**
 * Capability registry for the mobile-pinning workflow (`HomePage`: find/
 * create venue -> assign station -> review -> submit).
 *
 * See `web-dashboard`'s `station-capabilities.ts` for the full maintenance
 * protocol this mirrors. Every `anyOf` key below was verified against the
 * live service-rental `@Secured` annotations on 2026-07-13, except
 * `content_library.partner` (inferred from the `CONTENT_LIBRARY_PARTNER`
 * constant name via the same naming convention every other verified key
 * follows — flagged for a human eyeball before relying on it).
 *
 * No role check exists anywhere in this app today beyond authentication
 * (`RequireAuth`) — every capability's `legacy` is `'allow'`, so this
 * registry changes zero visible behavior while every rollout key stays OFF.
 * Only `pinning.workflow.access` is `wired: true` (gates the `/` route);
 * the rest are catalogued and ready, but have no existing UI gate to
 * replace yet — wiring them is a follow-up once there's an actual UI
 * moment (e.g. disabling a step's "Next" button) to hook into.
 */

export type PinningCapabilityName =
  | 'pinning.workflow.access'
  | 'pinning.venue.search'
  | 'pinning.venue.detail.view'
  | 'pinning.venueType.view'
  | 'pinning.station.search'
  | 'pinning.distributionChannel.view'
  | 'pinning.unpinningReason.view'
  | 'pinning.photo.upload'
  | 'pinning.submit';

export const PINNING_CAPABILITIES: Record<
  PinningCapabilityName,
  CapabilityRule<PinningCapabilityName>
> = {
  /**
   * Root: can the user use the pinning workflow at all.
   * Wired: App.tsx `/` index route (`<Can>` wrapping `<HomePage />`).
   * Mirrors the terminal submit action's own key, same simplification
   * `station.page.view` uses for its route in web-dashboard — reaching the
   * page without ever being able to submit is not meaningfully different
   * from not reaching it at all.
   * Backend: POST /api/venue-placement-mobile-pinning/create ->
   * location.pinning (CustomSecurityRule.USER — any authenticated role).
   */
  'pinning.workflow.access': {
    endpoints: ['POST /api/venue-placement-mobile-pinning/create'],
    anyOf: ['location.pinning'],
    wired: true,
    legacy: 'allow',
  },

  /**
   * Venue search/list (existing-venue lookup + Google Place existence check).
   * Wiring target: StartAndFindVenueStep.tsx.
   * POST /api/admin/venue/filter-list, POST /api/admin/venue/list-by-google-place-ids
   * -> location.view.partner.
   */
  'pinning.venue.search': {
    endpoints: [
      'POST /api/admin/venue/filter-list',
      'POST /api/admin/venue/list-by-google-place-ids',
    ],
    anyOf: ['location.view.partner'],
    requires: ['pinning.workflow.access'],
    wired: false,
    legacy: 'allow',
  },

  /**
   * Existing-venue detail + its current placements.
   * Wiring target: ConfirmVenueStep.tsx, VenueDetailReviewStep.tsx.
   * GET /api/admin/venue/detail, GET /api/venue-request/list-station-at ->
   * location.view.partner.
   */
  'pinning.venue.detail.view': {
    endpoints: [
      'GET /api/admin/venue/detail',
      'GET /api/venue-request/list-station-at',
    ],
    anyOf: ['location.view.partner'],
    requires: ['pinning.venue.search'],
    wired: false,
    legacy: 'allow',
  },

  /**
   * Venue type / sub-type dropdowns.
   * Wiring target: CreateVenueStep.tsx.
   * POST /api/venue-type/list -> config.partner.
   */
  'pinning.venueType.view': {
    endpoints: ['POST /api/venue-type/list'],
    anyOf: ['config.partner'],
    requires: ['pinning.workflow.access'],
    wired: false,
    legacy: 'allow',
  },

  /**
   * Unassigned-station search (QR scan or manual search).
   * Wiring target: ScanQR.tsx, SelectStationStep.tsx.
   * POST /api/station/list-with-unassigned-stations -> location.view.partner.
   */
  'pinning.station.search': {
    endpoints: ['POST /api/station/list-with-unassigned-stations'],
    anyOf: ['location.view.partner'],
    requires: ['pinning.workflow.access'],
    wired: false,
    legacy: 'allow',
  },

  /**
   * Distribution-channel dropdown.
   * Wiring target: SelectDistributionChannelStep.tsx. Same key already used
   * by web-dashboard's `config.placement.distributionChannelOptions` for
   * this exact endpoint.
   * POST /api/distribution-channel/list -> config.partner.
   */
  'pinning.distributionChannel.view': {
    endpoints: ['POST /api/distribution-channel/list'],
    anyOf: ['config.partner'],
    requires: ['pinning.workflow.access'],
    wired: false,
    legacy: 'allow',
  },

  /**
   * Swap/relocation/drop reason picker.
   * Wiring target: ConfirmSwapStation.tsx, ConfirmRelocationStep.tsx. Same
   * key already used by web-dashboard's `config.reason.setVenueStatus` for
   * this exact endpoint.
   * POST /api/unpinning-reason/list -> config.partner.
   */
  'pinning.unpinningReason.view': {
    endpoints: ['POST /api/unpinning-reason/list'],
    anyOf: ['config.partner'],
    requires: ['pinning.workflow.access'],
    wired: false,
    legacy: 'allow',
  },

  /**
   * Required-photo upload.
   * Wiring target: UploadPhotosStep.tsx.
   * POST /api/file/upload -> content_library.partner.
   */
  'pinning.photo.upload': {
    endpoints: ['POST /api/file/upload'],
    anyOf: ['content_library.partner'],
    requires: ['pinning.workflow.access'],
    wired: false,
    legacy: 'allow',
  },

  /**
   * Terminal submit of the full pinning request.
   * Wiring target: ReviewAndConfirmStep.tsx.
   * POST /api/venue-placement-mobile-pinning/create -> location.pinning.
   */
  'pinning.submit': {
    endpoints: ['POST /api/venue-placement-mobile-pinning/create'],
    anyOf: ['location.pinning'],
    requires: ['pinning.venue.search', 'pinning.station.search'],
    wired: false,
    legacy: 'allow',
  },
};
